import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import apiClient from '@/lib/axios';

export interface UserPreferenceData {
  clerkUserId: string;
  lastRestaurantId?: string | null;
  lastBranchId?: string | null;
  updatedAt: string;
}

/**
 * Hook to fetch user preferences from backend
 * Loads the user's last selected restaurant and branch
 */
export function useUserPreferences() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ['user-preferences'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) return null;

      try {
        const response = await apiClient.get('/user/preferences', {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data as UserPreferenceData | null;
      } catch (error) {
        console.warn('Failed to fetch user preferences:', error);
        return null; // Return null if not found or error
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!getToken, // Only run when token is available
  });
}

/**
 * Hook to save/update user preferences
 * Saves the currently selected restaurant and branch
 */
export function useSaveUserPreferences() {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (data: {
      restaurantId?: string;
      branchId?: string;
    }) => {
      console.log('[useSaveUserPreferences] Starting mutation with data:', data);
      const token = await getToken();
      console.log('[useSaveUserPreferences] Token obtained:', token ? 'Yes' : 'No');
      if (!token) throw new Error('No authentication token');

      console.log('[useSaveUserPreferences] Making PUT request to /user/preferences');
      const response = await apiClient.put('/user/preferences', data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[useSaveUserPreferences] Response received:', response.status, response.data);
      return response.data?.data as UserPreferenceData;
    },
    onSuccess: (data) => {
      console.log('[useSaveUserPreferences] Mutation successful:', data);
    },
    onError: (error) => {
      console.error('[useSaveUserPreferences] Mutation failed:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    },
  });
}
